/**
 * AuthenticationBridgeServiceModule is a class that handles authentication through a bridge or mock data.
 * It provides methods to check bridge availability, authenticate users, and manage user sessions.
 */
class AuthenticationBridgeServiceModule {
    /**
     * Constructs the AuthenticationBridgeServiceModule instance.
     * @param {Object} $signals - Signal service for emitting events.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $http - AngularJS HTTP service.
     */
    constructor($signals, $injector, $http, AuthenticationBridgeClient) {
        this.$signals = $signals;
        this.$injector = $injector;
        this.$http = $http;
        this.AuthenticationBridgeClient = AuthenticationBridgeClient;

        /**
         * Storage for the last used codes.
         * @type {Object}
         */
        this.lastCodeStorage = {};
    }

    /**
     * Checks if the authentication bridge is available.
     * @returns {boolean} - True if the bridge is available, false otherwise.
     */
    isBridgeAvailable() {
        return true;
    }

    /**
     * Gets the context of the authentication bridge.
     * @returns {Object} - The bridge context or an empty object if not available.
    getBridgeContext() {
        return window.jsBridgeAuthentication || {};
    }
    */
   
    /**
     * Retrieves the UUID of a mock user based on the mock unlock code.
     * @returns {string} - The UUID of the mock user.
    getMockUserUUID() {
        return this.mockUsers[this.getMockUserUnlockCode()];
    }
 */
    
    /**
     * Retrieves the mock user unlock code.
     * @returns {string} - The mock user unlock code.
    getMockUserUnlockCode() {
        return "001234567890";
    }
     */

    /**
     * Attempts to unlock synchronously in CMS mode.
     * @param {string} value - The value to use for unlocking.
     * @param {Function} callback - The callback function to execute after unlocking.
     */
    cms_tryUnlockSynchronous(value, callback) {
        if (!this.isCMS)
            return;

        this.$signals.emit("tryUnlockSuccess", this.getMockUserUUID());
        sessionStorage.userUUID = this.getMockUserUUID();

        callback();
    }

    /**
     * Zero-fills a number to a specified width.
     * @param {number} number - The number to zero-fill.
     * @param {number} width - The desired width of the number.
     * @returns {string} - The zero-filled number as a string.
     */
    zeroFill(number, width) {
        if (!angular.isDefined(number)) {
            number = "0";
        }
        width -= number.toString().length;
        if (width > 0) {
            return new Array(width + (/\./.test(number) ? 2 : 1)).join('0') + number;
        }
        return number + ""; // always return a string
    }

    /**
     * Gets the storage for the last used code.
     * @returns {Object} - The storage for the last used code.
     */
    getStorageForLastCode() {
        /*this.lastCodeStorage = this.isBridgeAvailable() ? this : sessionStorage;
        return this.lastCodeStorage;*/
        return sessionStorage;
    }

    /**
     * Attempts to authenticate an invigilator using the provided code.
     * @param {string} invigilatorCode - The invigilator code to authenticate.
     */
    tryInvigilatorAuthentication(invigilatorCode) {
        try {
            var lastCodeStorage = this.getStorageForLastCode(),
                invigilatorCodeZ = this.zeroFill(invigilatorCode, 6),
                schoolCodeZ = this.zeroFill(lastCodeStorage.lastSchoolCode, 6),
                unlockCodeZ = this.zeroFill(lastCodeStorage.lastUnlockCode, 4);

            var concatenatedCode = schoolCodeZ + "-" + invigilatorCodeZ + "-" + unlockCodeZ;

            console.log(concatenatedCode);
            console.log("%c%s", "color:red", concatenatedCode);

            if (this.isBridgeAvailable()) {
                var result = this.AuthenticationBridgeClient.tryInvigilatorAuthentication(concatenatedCode);
                return result;
            }

            // DEV MODE: Use mock data
            var mockUserUUID = this.mockUsers[concatenatedCode];
            if (typeof (mockUserUUID) == 'undefined')
                this.$signals.emit("tryInvigilatorAuthenticationFailed");
            else {
                var $config = this.$injector.get('$config');
                this.$http.get($config.getUserFileURL(mockUserUUID)).then(
                    function (response) {
                        this.$signals.emit("tryInvigilatorAuthenticationSuccess", JSON.stringify(response.data));
                    }.bind(this),
                    function (error) {
                        console.error(error);
                        this.$signals.emit("tryInvigilatorAuthenticationFailed");
                    }.bind(this)
                );
            }
        } catch (err) {
            console.error('Error: ', err);
            this.$signals.emit('ErrorHandler', 'error', err);
            this.$signals.emit('tryInvigilatorAuthenticationFailed');
        }
    }

    /**
     * Attempts to authenticate an invigilator using all provided codes.
     * @param {string} invigilatorCode - The invigilator code.
     * @param {string} schoolCode - The school code.
     * @param {string} unlockCode - The unlock code.
     * @returns {boolean} - True if authentication is successful, false otherwise.
     */
    tryInvigilatorAuthenticationWithAllCodes(invigilatorCode, schoolCode, unlockCode) {
        var lastCodeStorage = this.getStorageForLastCode();

        lastCodeStorage.lastSchoolCode = schoolCode;
        lastCodeStorage.lastUnlockCode = unlockCode;

        return this.tryInvigilatorAuthentication(invigilatorCode);
    }

    /**
     * Attempts to unlock using the provided codes.
     * @param {string} schoolCode - The school code.
     * @param {string} candidateCode - The candidate code.
     * @param {string} unlockCode - The unlock code.
     */
    tryUnlock(schoolCode, candidateCode, unlockCode) {
        var schoolCodeZ = this.zeroFill(schoolCode, 6);
        var candidateCodeZ = this.zeroFill(candidateCode, 6);
        var unlockCodeZ = this.zeroFill(unlockCode, 4);

        var concatenatedKey = schoolCodeZ + '-' + candidateCodeZ + '-' + unlockCodeZ;

        var lastCodeStorage = this.getStorageForLastCode();
        lastCodeStorage.lastSchoolCode = schoolCodeZ;
        lastCodeStorage.lastCandidateCode = candidateCodeZ;
        lastCodeStorage.lastUnlockCode = unlockCodeZ;

        if (this.isBridgeAvailable()) {
            return this.AuthenticationBridgeClient.tryUnlock(concatenatedKey);
        }

        if (this.isCMS) {
            console.info("isCMS: sending tryUnlockSuccess for ", this.mockUsers["001234567890"]);
            this.$signals.emit("tryUnlockSuccess", this.mockUsers["001234567890"]);
            sessionStorage.userUUID = this.mockUsers["001234567890"];
            return;
        }

        var mockUserUUID = this.mockUsers[concatenatedKey];
        if (typeof (mockUserUUID) == 'undefined')
            this.$signals.emit("tryUnlockFailed");
        else {
            this.$signals.emit("tryUnlockSuccess", mockUserUUID);
            sessionStorage.userUUID = mockUserUUID;
        }
    }

    /**
     * Checks if the user is already unlocked.
     * @returns {boolean} - True if the user is unlocked, false otherwise.
     */
    isUnlocked() {
        if (this.isBridgeAvailable()) {
            return this.AuthenticationBridgeClient.isUnlocked();
        }

        return typeof (sessionStorage.userUUID) != 'undefined';
    }

    /**
     * Retrieves the UUID of the current user.
     * @returns {string} - The UUID of the current user.
     */
    getUserUUID() {
        if (this.isBridgeAvailable()) {
            return this.AuthenticationBridgeClient.getUserUUID();
        }

        return sessionStorage.userUUID;
    }
}

export default AuthenticationBridgeServiceModule;